import { useState, type ComponentProps } from 'react';
import { parsePoundsToPence, penceToInput } from '../../lib/format';
import { Input } from './Input';

export interface MoneyInputProps
  extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'defaultValue' | 'type' | 'prefix' | 'width'> {
  /** Integer pence (FR-3.6). `null` = empty or not a valid amount yet. */
  value: number | null | undefined;
  onChange: (pence: number | null) => void;
  /** Allow negative amounts (e.g. refunds). Default false. */
  allowNegative?: boolean;
  width?: 'full' | 'sm' | 'md';
}

/**
 * Pounds entry ↔ integer pence value. People type "1,234.56" or "£1234.5"; the form value is 123456.
 * Shows a "£" prefix, uses the decimal keypad on phones and tidies the text on blur ("1,234.50").
 * Invalid text (letters, 3+ decimal places) reports `null` and sets `data-invalid-text` so the
 * zod schema's "Enter an amount" error shows. Use with react-hook-form's `Controller`.
 */
export function MoneyInput({ value, onChange, onBlur, allowNegative = false, width = 'md', ...rest }: MoneyInputProps) {
  const [text, setText] = useState(() => penceToInput(value));
  const [prevValue, setPrevValue] = useState(value);

  // Sync when the value changes from outside (form reset, async load) — "adjust state during render" pattern.
  if (value !== prevValue) {
    setPrevValue(value);
    const current = parsePoundsToPence(text, { allowNegative });
    if ((value ?? null) !== current) setText(penceToInput(value));
  }

  const parsed = parsePoundsToPence(text, { allowNegative });
  const invalidText = text.trim() !== '' && parsed === null;

  return (
    <Input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      prefix="£"
      width={width}
      value={text}
      data-invalid-text={invalidText || undefined}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const pence = parsePoundsToPence(next, { allowNegative });
        setPrevValue(pence);
        onChange(pence);
      }}
      onBlur={(e) => {
        const pence = parsePoundsToPence(text, { allowNegative });
        if (pence !== null) setText(penceToInput(pence));
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}
